import { useTheme } from 'styled-components';
import React, { useEffect, useState, useRef, forwardRef } from 'react';

import { dispatchOnChangeEvent, isPassword } from './helpers';

import { InputIcon, PasswordToggle, ClearButton, HelperText } from './subComponents';

import { InputChangeEvent } from '../types';

import * as S from './styles';
import { IInput } from './index';
import { Text } from '../Text';

export const Input = forwardRef<HTMLInputElement, IInput>(
    (
        {
            type: initialType = 'text',
            name = '',
            errorStatus = false,
            successStatus = false,
            touched = true,
            placeholder = '',
            errorMessage = '',
            successMessage = '',
            helperMessage = '',
            label = '',
            leftIcon,
            rightIcon,
            iconSize = 14,
            onChange,
            onBlur,
            disabled,
            onFocus,
            unit,
            dataLocator,
            className,
            initialValue = '',
            value: externalValue,
            onClear,
            wrapperMarginBottom,
        },
        ref
    ) => {
        const theme = useTheme();
        const isControlled = externalValue !== undefined;
        const clearButtonRef = useRef(null);
        const passwordToggleRef = useRef(null);

        const [isFocus, setIsFocus] = useState(false);
        const [actualType, setActualType] = useState(initialType);
        const [internalValue, setInternalValue] = useState(initialValue);

        useEffect(() => {
            if (initialValue !== value) {
                setInternalValue(initialValue);
            }
        }, [initialValue]);

        useEffect(() => {
            setActualType(initialType);
        }, [initialType]);

        const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
            const isClearButton = e.relatedTarget === clearButtonRef.current;
            const isPasswordToggle = e.relatedTarget === passwordToggleRef.current;

            if (e.relatedTarget && (isClearButton || isPasswordToggle)) {
                e.preventDefault();
                (ref as React.RefObject<HTMLInputElement>)?.current?.focus();
            } else {
                setIsFocus(false);
                if (onBlur) onBlur(e);
            }
        };

        const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (!isFocus) setIsFocus(true);
            const incomeValue = e.target.value;

            if (!isControlled) setInternalValue(incomeValue);
            if (onChange) onChange(e);
        };

        const onFocusHandler = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsFocus(true);
            if (onFocus) onFocus(e);
        };

        const onClearHandler = (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();
            (ref as React.RefObject<HTMLInputElement>)?.current?.focus();

            if (!isControlled) setInternalValue('');
            if (isControlled) {
                const inputEl = (ref as React.RefObject<HTMLInputElement>)?.current;

                if (inputEl) {
                    dispatchOnChangeEvent(inputEl, '');
                }
            }
            if (onClear) onClear();
        };

        const value = isControlled ? externalValue : internalValue;
        const displayLabel = label && (isFocus || (!isFocus && Boolean(value)));

        return (
            <S.InputWrapper wrapperMarginBottom={wrapperMarginBottom}>
                {displayLabel && (
                    <S.InputLabel theme={theme} $touched={touched} htmlFor={name}>
                        {label}
                    </S.InputLabel>
                )}

                <S.InputBox>
                    {Boolean(leftIcon) && (
                        <S.LeftBox>
                            <InputIcon disabled={Boolean(disabled)} icon={leftIcon} size={iconSize} isFocus={isFocus} />
                        </S.LeftBox>
                    )}

                    <S.RightBox>
                        {Boolean(unit) && (
                            <S.UnitBox $isFocus={isFocus} $isRightIcon={Boolean(rightIcon)}>
                                <Text color="text-secondary-subdued" type="medium-text">
                                    {unit}
                                </Text>
                            </S.UnitBox>
                        )}

                        {isPassword(initialType) ? (
                            <PasswordToggle
                                ref={passwordToggleRef}
                                disabled={Boolean(disabled)}
                                inputType={actualType}
                                changeType={setActualType}
                                isFocus={isFocus}
                            />
                        ) : (
                            <InputIcon disabled={Boolean(disabled)} icon={rightIcon} size={iconSize} isFocus />
                        )}
                        <ClearButton ref={clearButtonRef} onClearHandler={onClearHandler} isFocus={isFocus} />
                    </S.RightBox>

                    <S.StyledInput
                        theme={theme}
                        placeholder={placeholder}
                        $errorStatus={errorStatus}
                        $successStatus={successStatus}
                        type={actualType}
                        name={name}
                        id={name}
                        $leftIcon={leftIcon}
                        $rightIcon={rightIcon}
                        $touched={touched}
                        onChange={onChangeHandler}
                        onBlur={onBlurHandler}
                        ref={ref}
                        onFocus={onFocusHandler}
                        disabled={disabled}
                        $unit={unit}
                        $isFocus={isFocus}
                        value={value}
                        data-locator={dataLocator}
                        data-testid={dataLocator}
                        className={className}
                    />
                </S.InputBox>

                <HelperText
                    {...{
                        errorStatus,
                        successStatus,
                        touched,
                        helperMessage,
                        errorMessage,
                        successMessage,
                    }}
                />
            </S.InputWrapper>
        );
    }
);
